// File: Step1BasicInfo.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

interface StepProps {
  formData: {
    name: string;
    sku: string;
    shortDescription: string;
    description: string;
  };
  onInputChange: (field: keyof StepProps['formData'], value: string) => void;
  isSkuManual?: boolean;
  onSkuManualChange?: (manual: boolean) => void;
}

interface DescriptionResult {
  shortDescription: string;
  fullDescription: string;
}

// نموذج بديل فعال (محرك GPT صغير)
const GPT_MODEL = 'distilgpt2'; // أو 'gpt2-medium' إذا كان لديك كريدت

// وظيفة توليد الوصف باستخدام axios
const generateDescription = async (name: string): Promise<DescriptionResult> => {
  if (!name) return { shortDescription: '', fullDescription: '' };

  try {
    const prompt = `
    Generate a detailed, professional product description in BOTH Arabic and English 
    for a product named: "${name}".
    
    Rules:
    1. First section in Arabic (باللغة العربية)
    2. Second section in English
    3. Include features, benefits, and specifications
    4. Provide a short description (under 160 characters)
    5. The full description should be comprehensive
    
    Format your response as JSON:
    {
      "shortDescription": "brief description here under 160 chars",
      "fullDescription": "detailed description here with Arabic and English sections"
    }
    `;

    const response = await axios.post(
      `https://api-inference.huggingface.co/models/${GPT_MODEL}`,
      { 
        inputs: prompt,
        parameters: {
          max_length: 500,
          temperature: 0.7,
          top_p: 0.9,
          return_full_text: false
        }
      },
      {
        headers: {
          'Authorization': `Bearer ${process.env.REACT_APP_HF_API_KEY}`,
          'Content-Type': 'application/json',
        },
        timeout: 30000, // 30 ثانية timeout
      }
    );

    const data:any = response.data;
    
    // معالجة الاستجابة المختلفة من Hugging Face API
    let generatedText = '';
    
    if (Array.isArray(data) && data[0] && data[0].generated_text) {
      generatedText = data[0].generated_text;
    } else if (data.generated_text) {
      generatedText = data.generated_text;
    } else if (typeof data === 'string') {
      generatedText = data;
    }
    
    // محاولة استخراج JSON
    try {
      const jsonStart = generatedText.indexOf('{');
      const jsonEnd = generatedText.lastIndexOf('}') + 1;
      
      if (jsonStart !== -1 && jsonEnd !== -1) {
        const jsonString = generatedText.slice(jsonStart, jsonEnd);
        const parsed = JSON.parse(jsonString);
        
        return {
          shortDescription: parsed.shortDescription?.substring(0, 160) || '',
          fullDescription: parsed.fullDescription || generatedText,
        };
      }
    } catch (jsonError) {
      console.log('Could not parse JSON, using fallback', jsonError);
    }

    // Fallback: تقسيم النص إذا كان طويلاً
    const shortDesc = generatedText.substring(0, 160).trim();
    
    // تحسين الوصف الكامل مع تقسيمه إلى أقسام
    const fullDesc = `=== Arabic / العربية ===\n\n${generatedText}\n\n=== English ===\n\n${generatedText}`;
    
    return {
      shortDescription: shortDesc,
      fullDescription: fullDesc,
    };
    
  } catch (err: any) {
    console.error('Failed to generate description', err);
    
    // Fallback محلي إذا فشل API
    const fallbackDesc = {
      shortDescription: `منتج ${name} - جودة عالية مع ضمان الجودة`,
      fullDescription: `الوصف بالعربية:\n${name} هو منتج عالي الجودة مصمم لتلبية احتياجات العملاء. يتميز بمجموعة من المزايا والمواصفات الفنية التي تضمن الأداء المتميز.\n\nالوصف بالإنجليزية:\n${name} is a high-quality product designed to meet customer needs. It features a range of advantages and technical specifications that ensure excellent performance.`
    };
    
    return fallbackDesc;
  }
};

const buildSku = (name: string) =>
  name
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim();

const Step1BasicInfo: React.FC<StepProps> = ({
  formData,
  onInputChange,
  isSkuManual = false,
  onSkuManualChange,
}) => {
  const { t } = useTranslation();

  const [state, setState] = useState(formData);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResult, setAiResult] = useState<DescriptionResult | null>(null);
  const [aiError, setAiError] = useState<string | null>(null);

  const updateField = useCallback(
    (field: keyof typeof state, value: string) => {
      setState((prev) => ({ ...prev, [field]: value }));
      onInputChange(field, value);
    },
    [onInputChange]
  );

  useEffect(() => {
    if (!isSkuManual && state.name) {
      updateField('sku', buildSku(state.name));
    }
  }, [state.name, isSkuManual]);

  const handleGenerateDescription = useCallback(async () => {
    if (!state.name) return;

    setAiLoading(true);
    setAiError(null);
    setAiResult(null);

    try {
      const result = await generateDescription(state.name);
      setAiResult(result);
      
      updateField('shortDescription', result.shortDescription);
      updateField('description', result.fullDescription);
      
    } catch (error: any) {
      console.error('AI generation failed', error);
      setAiError(error.message || 'Failed to generate description');
      
      // استخدام وصف بديل
      const fallbackResult = {
        shortDescription: `${state.name} - منتج مبتكر ذو جودة عالية`,
        fullDescription: `الوصف بالعربية:\n${state.name} هو منتج مصمم بعناية ليقدم تجربة استثنائية للعملاء. يجمع بين الجمال والوظائف لتلبية جميع احتياجاتك.\n\nالوصف بالإنجليزية:\n${state.name} is a carefully designed product that offers an exceptional customer experience. It combines beauty and functionality to meet all your needs.`
      };
      
      updateField('shortDescription', fallbackResult.shortDescription);
      updateField('description', fallbackResult.fullDescription);
      setAiResult(fallbackResult);
      
    } finally {
      setAiLoading(false);
    }
  }, [state.name, updateField]);

  return (
    <div className="space-y-6">
      <h3 className="text-xl font-semibold text-gray-800">
        {t('Step1BasicInfo.section.title', 'Tell us about your product')}
      </h3>

      {/* Product Name */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t('Step1BasicInfo.section.productName.label', 'Product name *')}
        </label>
        <input
          type="text"
          value={state.name}
          onChange={(e) => updateField('name', e.target.value)}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
          placeholder={t(
            'Step1BasicInfo.section.productName.placeholder',
            'e.g. Premium Coffee Beans'
          )}
        />
        <p className="text-xs text-gray-500 mt-1">
          {t('Step1BasicInfo.section.productName.hint', 'Enter product name to enable AI description generation')}
        </p>
      </div>

      {/* SKU */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t('Step1BasicInfo.section.sku.label', 'SKU *')}
        </label>
        <div className="flex gap-2">
          <input
            type="text"
            value={state.sku}
            onChange={(e) => {
              onSkuManualChange?.(true);
              updateField('sku', e.target.value);
            }}
            className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
            placeholder="product-sku-123"
          />
          <button
            type="button"
            onClick={() => onSkuManualChange?.(false)}
            className="px-4 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 border border-gray-300 transition"
          >
            {t('Step1BasicInfo.section.sku.auto', 'Auto')}
          </button>
        </div>
        <p className="text-xs text-gray-500 mt-1">
          {t('Step1BasicInfo.section.sku.hint', 'SKU will auto-generate from product name')}
        </p>
      </div>

      {/* AI Button Section */}
      <div className="space-y-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={handleGenerateDescription}
            disabled={!state.name || aiLoading}
            className="px-5 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg hover:from-purple-700 hover:to-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition shadow-md hover:shadow-lg"
          >
            {aiLoading ? (
              <>
                <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {t('Step1BasicInfo.section.ai.loading', 'جاري توليد الوصف باستخدام الذكاء الاصطناعي...')}
              </>
            ) : (
              <>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
                </svg>
                {t('Step1BasicInfo.section.ai.button', 'توليد الوصف باستخدام الذكاء الاصطناعي')}
              </>
            )}
          </button>
          
          {aiResult && !aiLoading && (
            <span className="text-green-600 text-sm flex items-center gap-1 animate-pulse">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path>
              </svg>
              {t('Step1BasicInfo.section.ai.success', 'تم توليد الوصف بنجاح')}
            </span>
          )}
        </div>
        
        {/* Status Messages */}
        {aiError && (
          <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 p-2 rounded">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd"></path>
            </svg>
            <span>{aiError}</span>
            <button 
              onClick={() => setAiError(null)}
              className="ml-auto text-gray-500 hover:text-gray-700"
            >
              ✕
            </button>
          </div>
        )}
        
        {/* AI Loading Progress */}
        {aiLoading && (
          <div className="space-y-2 animate-fadeIn">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-purple-600 text-sm">
                <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {t('Step1BasicInfo.section.ai.processing', 'جاري معالجة الطلب...')}
              </div>
              <span className="text-xs text-gray-500">قد يستغرق بضع ثوانٍ</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div className="bg-gradient-to-r from-purple-500 to-indigo-500 h-2 rounded-full animate-pulse"></div>
            </div>
          </div>
        )}
        
        {/* AI Info */}
        {!aiLoading && (
          <div className="text-xs text-gray-500 flex items-center gap-1">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd"></path>
            </svg>
            {t('Step1BasicInfo.section.ai.info', 'سيقوم الذكاء الاصطناعي بإنشاء وصف عربي وإنجليزي تلقائياً')}
          </div>
        )}
      </div>

      {/* Short Description */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="block text-sm font-medium text-gray-700">
            {t('Step1BasicInfo.section.shortDescription.label', 'Short description')}
          </label>
          {aiResult && !aiLoading && (
            <span className="text-xs text-purple-600 bg-purple-50 px-2 py-1 rounded flex items-center gap-1">
              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                <path d="M11 17a1 1 0 001.447.894l4-2A1 1 0 0017 15V9.236a1 1 0 00-1.447-.894l-4 2a1 1 0 00-.553.894V17zM15.211 6.276a1 1 0 000-1.788l-4.764-2.382a1 1 0 00-.894 0L4.789 4.488a1 1 0 000 1.788l4.764 2.382a1 1 0 00.894 0l4.764-2.382zM4.447 8.342A1 1 0 003 9.236V15a1 1 0 00.553.894l4 2A1 1 0 009 17v-5.764a1 1 0 00-.553-.894l-4-2z"></path>
              </svg>
              {t('Step1BasicInfo.section.ai.generated', 'تم إنشاؤه بواسطة الذكاء الاصطناعي')}
            </span>
          )}
        </div>
        <textarea
          rows={2}
          maxLength={160}
          value={state.shortDescription}
          onChange={(e) => updateField('shortDescription', e.target.value)}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
          placeholder={t('Step1BasicInfo.section.shortDescription.placeholder', 'وصف قصير للمنتج (لا يتعدى 160 حرفاً)')}
        />
        <div className="flex justify-between items-center mt-1">
          <div className="text-xs text-gray-500">
            {t('Step1BasicInfo.section.shortDescription.hint', 'يظهر في بطاقات المنتج والنتائج السريعة')}
          </div>
          <div className={`text-xs ${state.shortDescription.length >= 160 ? 'text-red-500' : 'text-gray-500'}`}>
            {state.shortDescription.length}/160
          </div>
        </div>
      </div>

      {/* Full Description */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="block text-sm font-medium text-gray-700">
            {t('Step1BasicInfo.section.fullDescription.label', 'Full description')}
          </label>
          {aiResult && !aiLoading && (
            <span className="text-xs text-purple-600 bg-purple-50 px-2 py-1 rounded flex items-center gap-1">
              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                <path d="M11 17a1 1 0 001.447.894l4-2A1 1 0 0017 15V9.236a1 1 0 00-1.447-.894l-4 2a1 1 0 00-.553.894V17zM15.211 6.276a1 1 0 000-1.788l-4.764-2.382a1 1 0 00-.894 0L4.789 4.488a1 1 0 000 1.788l4.764 2.382a1 1 0 00.894 0l4.764-2.382zM4.447 8.342A1 1 0 003 9.236V15a1 1 0 00.553.894l4 2A1 1 0 009 17v-5.764a1 1 0 00-.553-.894l-4-2z"></path>
              </svg>
              {t('Step1BasicInfo.section.ai.generated', 'تم إنشاؤه بواسطة الذكاء الاصطناعي')}
            </span>
          )}
        </div>
        <textarea
          rows={8}
          value={state.description}
          onChange={(e) => updateField('description', e.target.value)}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition font-mono text-sm"
          placeholder={t('Step1BasicInfo.section.fullDescription.placeholder', 'الوصف الكامل للمنتج باللغتين العربية والإنجليزية...')}
        />
        <div className="text-xs text-gray-500 mt-1">
          {t('Step1BasicInfo.section.fullDescription.hint', 'يمكنك تعديل النص المُولد يدوياً')}
        </div>
      </div>
    </div>
  );
};

export default Step1BasicInfo;